"use client";

import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES, VIDEO_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";

const UserAuth = dynamic(async () => (await import("./UserAuth")).UserAuth, {
  ssr: false,
});

export const userAuthInputs = [
  {
    name: "consents",
    type: "object",
    defaultValue: {
      oneTrustErrorMessage: "",
      oneTrustErrorLinkText: "",
      oneTrustErrorRedirectUrl: "",
      legalConditionsForConsents: "",
      consentOptionsListType: "default",
    },
    subFields: [
      {
        name: "oneTrustErrorMessage",
        type: "richText",
      },
      {
        name: "oneTrustErrorLinkText",
        type: "string",
      },
      {
        name: "oneTrustErrorRedirectUrl",
        type: "url",
        required: true,
      },
      {
        name: "legalConditionsForConsents",
        type: "string",
        required: true,
        defaultValue: "",
      },
      {
        name: "consentOptionsListType",
        type: "string",
        enum: ["default", "extended"],
      },
    ],
  },
  {
    name: "ageVerificationMethodImages",
    type: "reference",
    model: getBuilderModelName("age-verification-method-images"),
  },
  {
    name: "deviceRegistration",
    type: "object",
    defaultValue: {
      isDeviceRegistrationMandatory: false,
      isDeviceRegistrationByLeafletEnabled: false,
      isScanDeviceQrEnabled: false,
      deviceCodeLength: "short",
    },
    subFields: [
      {
        name: "isDeviceRegistrationMandatory",
        type: "boolean",
      },
      {
        name: "isDeviceRegistrationByLeafletEnabled",
        type: "boolean",
      },
      {
        name: "isScanDeviceQrEnabled",
        type: "boolean",
      },
      {
        name: "deviceCodeLength",
        type: "string",
        enum: ["short", "long", "gen4"],
        required: true,
      },
      {
        name: "desktopVideoTutorialUrl",
        type: "file",
        allowedFileTypes: VIDEO_FILE_TYPES,
        friendlyName: "Desktop video tutorial",
      },
      {
        name: "mobileVideoTutorialUrl",
        type: "file",
        friendlyName: "Mobile video tutorial",
        allowedFileTypes: VIDEO_FILE_TYPES,
      },
      {
        name: "posterTutorialUrl",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
    ],
  },
  {
    name: "genderInformation",
    type: "object",
    subFields: [
      {
        name: "isGenderEnabled",
        type: "boolean",
      },
    ],
  },
  {
    name: "registrationSuccessPage",
    type: "object",
    subFields: [
      {
        name: "successBannerMessage",
        type: "richText",
      },
      {
        name: "hidePloomClubBanner",
        type: "boolean",
      },
      {
        name: "learnMoreLinkText",
        type: "string",
      },
      {
        name: "disclaimer",
        type: "string",
      },
      {
        name: "learnMoreLinkUrl",
        type: "url",
      },
    ],
  },
  {
    name: "loyaltyCardSuccessPage",
    type: "object",
    subFields: [
      {
        name: "isLoyaltyCardSuccessPageEnabled",
        type: "boolean",
        required: true,
      },
      {
        name: "isPloomBenefitsBannerHidden",
        type: "boolean",
        required: true,
      },
    ],
  },
  {
    name: "offlineRegistration",
    type: "object",
    subFields: [
      {
        name: "isAvEnabled",
        type: "boolean",
        friendlyName: "Is AV enabled for NON AV register flow?",
      },
      {
        name: "isDeviceRegistrationEnabled",
        type: "boolean",
        friendlyName:
          "Is device registration step enabled for NON AV register flow?",
      },
    ],
  },
  {
    name: "registrationCommon",
    type: "object",
    subFields: [
      {
        name: "isRegistrationClosableBeforeAv",
        type: "boolean",
      },
      {
        name: "isPostCodeNotRequired",
        type: "boolean",
      },
      {
        name: "isAutocompleteDisabled",
        type: "boolean",
      },
      {
        name: "isAgeVerificationUuidFromCookieRequired",
        type: "boolean",
      },
      {
        name: "avDisclaimerMessage",
        type: "richText",
      },
    ],
  },
  {
    name: "phoneValidationOtp",
    type: "object",
    subFields: [
      {
        name: "successValidationViaSmsMessage",
        type: "richText",
      },
      {
        name: "failedValidationViaSmsMessage",
        type: "richText",
      },
      {
        name: "isOtpNumeric",
        type: "boolean",
      },
    ],
  },
  {
    name: "emailValidation",
    type: "object",
    subFields: [
      {
        name: "isOtpNumeric",
        type: "boolean",
      },
    ],
  },
  {
    name: "commonLoginSettings",
    type: "object",
    subFields: [
      {
        name: "socialLoginSeparatorText",
        type: "string",
      },
      {
        name: "loginFormMode",
        type: "string",
        required: true,
        enum: ["email", "phone", "email_and_phone"],
        defaultValue: "email",
      },
      {
        name: "loginFormModeDefaultTab",
        type: "string",
        required: true,
        enum: ["email", "phone"],
        defaultValue: "email",
      },
      {
        name: "isKeepMeLoggedInEnabled",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
] as const satisfies Inputs;

export const UserAuthComponent: RegisteredComponent = {
  name: "UserAuth",
  component: UserAuth,
  inputs: userAuthInputs,
};
